<template>
  <b-container fluid class="admin-orders">
    <admin-preloader :show="loading"/>
    <admin-breadcrumb name="Orders"/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Orders" with-footer>
          <admin-orders-table :orders="orders"/>
          <template v-slot:header-right>
            <div class="mt-n2 d-flex flex-wrap admin-order-filters">
              <div class="mt-1 mr-2">
                <div>Order #</div>
                <b-input
                    class="mt-1 orders-filter-input"
                    v-model.trim="filters.number"
                    type="number"
                    no-wheel
                    autocomplete="off"
                    style="width: 100px"
                    @input="value => inputFilterChanged('number', value)"
                />
              </div>
              <div class="mt-1 mr-2">
                <div>Seller</div>
                <div style="width: 200px">
                  <b-dropdown
                      class="mt-1 orders-filter-dropdown"
                      variant="outline-secondary"
                      :text="sellersFilterLabel"
                      block
                  >
                    <div style="max-height: 60vh; overflow-y: auto;">
                      <b-dropdown-item-button
                          @click="toggleFilterValue('sellers', '')"
                          :active="filters.sellers.indexOf('') >= 0"
                      >
                        All
                      </b-dropdown-item-button>
                      <div
                          v-for="(company, key) in licensesByCompanies"
                          :key="key"
                      >
                        <b-dropdown-divider/>
                        <b-dropdown-group :header="company.company_name">
                          <b-dropdown-item-button
                              v-for="(option, key) in company.licenses"
                              :key="key"
                              @click="toggleFilterValue('sellers', option.value)"
                              :active="filters.sellers.indexOf(option.value) >= 0"
                          >
                            <b-icon-check v-if="option.value !== '' && filters.sellers.indexOf(option.value) >= 0"/>
                            {{ option.text }}
                          </b-dropdown-item-button>
                        </b-dropdown-group>
                      </div>
                    </div>
                  </b-dropdown>
                </div>
              </div>
              <div class="mt-1 mr-2">
                <div>Buyer</div>
                <div style="width: 200px">
                  <b-dropdown
                      class="mt-1 orders-filter-dropdown"
                      variant="outline-secondary"
                      :text="buyersFilterLabel"
                      block
                  >
                    <div style="max-height: 60vh; overflow-y: auto;">
                      <b-dropdown-item-button
                          @click="toggleFilterValue('buyers', '')"
                          :active="filters.buyers.indexOf('') >= 0"
                      >
                        All
                      </b-dropdown-item-button>
                      <div
                          v-for="(company, key) in licensesByCompanies"
                          :key="key"
                      >
                        <b-dropdown-divider/>
                        <b-dropdown-group :header="company.company_name">
                          <b-dropdown-item-button
                              v-for="(option, key) in company.licenses"
                              :key="key"
                              @click="toggleFilterValue('buyers', option.value)"
                              :active="filters.buyers.indexOf(option.value) >= 0"
                          >
                            <b-icon-check v-if="option.value !== '' && filters.buyers.indexOf(option.value) >= 0"/>
                            {{ option.text }}
                          </b-dropdown-item-button>
                        </b-dropdown-group>
                      </div>
                    </div>
                  </b-dropdown>
                </div>
              </div>
              <div class="mt-1 mr-2">
                <div>Source</div>
                <div style="width: 120px">
                  <b-dropdown
                      class="mt-1 orders-filter-dropdown"
                      variant="outline-secondary"
                      :text="sourcesFilterLabel"
                      block
                  >
                    <b-dropdown-item-button
                        v-for="(option, key) in orderSourcesOptions"
                        :key="key"
                        @click="toggleFilterValue('sources', option.value)"
                        :active="filters.sources.indexOf(option.value) >= 0"
                    >
                      <b-icon-check v-if="option.value !== '' && filters.sources.indexOf(option.value) >= 0"/>
                      {{ option.text }}
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </div>
              <div class="mt-1 mr-2">
                <div>Type</div>
                <div style="width: 120px">
                  <b-dropdown
                      class="mt-1 orders-filter-dropdown"
                      variant="outline-secondary"
                      :text="typesFilterLabel"
                      block
                  >
                    <b-dropdown-item-button
                        v-for="(option, key) in orderTypesOptions"
                        :key="key"
                        @click="toggleFilterValue('types', option.value)"
                        :active="filters.types.indexOf(option.value) >= 0"
                    >
                      <b-icon-check v-if="option.value !== '' && filters.types.indexOf(option.value) >= 0"/>
                      {{ option.text }}
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </div>
              <div class="mt-1 mr-2">
                <div>Status</div>
                <div style="width: 150px">
                  <b-dropdown
                      class="mt-1 orders-filter-dropdown"
                      variant="outline-secondary"
                      :text="statusesFilterLabel"
                      block
                  >
                    <b-dropdown-item-button
                        v-for="(option, key) in orderStatusesOptions"
                        :key="key"
                        @click="toggleFilterValue('statuses', option.value)"
                        :active="filters.statuses.indexOf(option.value) >= 0"
                    >
                      <b-icon-check v-if="option.value !== '' && filters.statuses.indexOf(option.value) >= 0"/>
                      {{ option.text }}
                    </b-dropdown-item-button>
                  </b-dropdown>
                </div>
              </div>
              <div class="mt-1 mr-2">
                <div>Start Date</div>
                <b-form-datepicker
                    v-model="filters.date_start"
                    class="mt-1"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    locale="en-US"
                    label-no-date-selected="Choose date"
                    today-button
                    reset-button
                    close-button
                    label-today-button="Today"
                    label-reset-button="Clear"
                    style="width: 140px"
                    :max="max_date"
                    @input="(date) => dateFilterChanged('start', date)"
                />
              </div>
              <div class="mt-1 mr-2">
                <div>End Date</div>
                <b-form-datepicker
                    v-model="filters.date_end"
                    class="mt-1"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    locale="en-US"
                    label-no-date-selected="Choose date"
                    today-button
                    reset-button
                    close-button
                    label-today-button="Today"
                    label-reset-button="Clear"
                    style="width: 140px"
                    :max="max_date"
                    @input="(date) => dateFilterChanged('end', date)"
                />
              </div>
              <div class="mt-1">
                <div>Show</div>
                <b-form-select
                    class="w-auto mt-1 orders-filter-select"
                    v-model="per_page"
                    :options="per_page_select_options"
                    @change="perPageFilterChanged"
                />
              </div>
            </div>
          </template>
          <template v-slot:footer>
            <b-pagination
                v-if="total_in_pages > 0"
                v-model="page"
                :total-rows="total_in_pages"
                :per-page="per_page"
                @change="loadOrders"
                align="center"
                class="admin-pagination mb-0"
            />
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader.vue";
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import {API_ROUTES} from "../api";
import AdminOrdersTable from "../components/AdminOrdersTable.vue";
import moment from "moment/moment";

export default {
  name: "AdminOrders",
  components: {AdminOrdersTable, AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    let currentPage = parseInt(this.$route.query["page"] ?? 1),
        perPage = parseInt(this.$route.query["per_page"] ?? 10),
        arrayQuery = {
          brands: this.$route.query["brands[]"] ?? '',
          sources: this.$route.query["sources[]"] ?? '',
          statuses: this.$route.query["statuses[]"] ?? '',
          types: this.$route.query["types[]"] ?? '',
          sellers: this.$route.query["sellers[]"] ?? '',
          buyers: this.$route.query["buyers[]"] ?? '',
        };

    return {
      orders: [],
      licenses: [],
      loading: true,
      page: currentPage,
      per_page: perPage,
      default_per_page: 10,
      total_in_pages: 0,
      filters: {
        number: this.$route.query["number"] ?? "",
        sources: Array.isArray(arrayQuery.sources) ? arrayQuery.sources : [arrayQuery.sources],
        statuses: Array.isArray(arrayQuery.statuses) ? arrayQuery.statuses : [arrayQuery.statuses],
        types: Array.isArray(arrayQuery.types) ? arrayQuery.types : [arrayQuery.types],
        date_start: this.$route.query["date_start"] ?? "",
        date_end: this.$route.query["date_end"] ?? "",
        sellers: Array.isArray(arrayQuery.sellers) ? arrayQuery.sellers : [arrayQuery.sellers],
        buyers: Array.isArray(arrayQuery.buyers) ? arrayQuery.buyers : [arrayQuery.buyers],
      },
      prev_filters: {
        number: this.$route.query["number"] ?? "",
      },
      timeouts: {
        number: null
      },
      per_page_select_options: [
        {
          value: 10,
          text: "10",
        },
        {
          value: 20,
          text: "20",
        },
        {
          value: 50,
          text: "50",
        },
      ],
      max_date: new Date()
    }
  },
  computed: {
    orderStatusesOptions() {
      return [
        {
          value: "",
          text: "All",
        },
        ...this.$store.state.data.order_statuses.map(status => ({
          value: status.id.toString(),
          text: status.name,
        }))
      ];
    },
    orderTypesOptions() {
      return [
        {
          value: "",
          text: "All",
        },
        ...this.$store.state.data.order_types.map(type => ({
          value: type.id.toString(),
          text: type.name,
        }))
      ];
    },
    orderSourcesOptions() {
      return [
        {
          value: "",
          text: "All",
        },
        ...this.$store.state.data.order_sources.map(source => ({
          value: source.id.toString(),
          text: source.name,
        }))
      ];
    },
    sourcesFilterLabel() {
      if (this.filters.sources.length === 1) {
        return this.orderSourcesOptions.find(o => o.value === this.filters.sources[0])?.text;
      }
      return this.filters.sources.length + ' selected';
    },
    typesFilterLabel() {
      if (this.filters.types.length === 1) {
        return this.orderTypesOptions.find(o => o.value === this.filters.types[0])?.text;
      }
      return this.filters.types.length + ' selected';
    },
    statusesFilterLabel() {
      if (this.filters.statuses.length === 1) {
        return this.orderStatusesOptions.find(o => o.value === this.filters.statuses[0])?.text;
      }
      return this.filters.statuses.length + ' selected';
    },
    sellersFilterLabel() {
      if (this.filters.sellers.length === 1) {
        if (this.filters.sellers[0] === '') {
          return 'All';
        }
        let license = this.licenses.find(l => l.id.toString() === this.filters.sellers[0]);
        if (license) {
          return license.facility.name;
        }
      }
      return this.filters.sellers.length + ' selected';
    },
    buyersFilterLabel() {
      if (this.filters.buyers.length === 1) {
        if (this.filters.buyers[0] === '') {
          return 'All';
        }
        let license = this.licenses.find(l => l.id.toString() === this.filters.buyers[0]);
        if (license) {
          return license.facility.name;
        }
      }
      return this.filters.buyers.length + ' selected';
    },
    licensesByCompanies() {
      let licensesByCompanies = [];
      if (this.licenses.length > 0) {
        let companies = {};
        this.licenses.sort((first, second) => {
          let name1 = first.company.name.toLowerCase(),
              name2 = second.company.name.toLowerCase();
          if (name1 < name2) {
            return -1;
          }
          if (name1 > name2) {
            return 1;
          }
          return 0;
        }).forEach(l => {
          if (typeof companies[l.company.id] === 'undefined') {
            companies[l.company.id] = {
              company_name: l.company.name,
              licenses: []
            };
          }
          companies[l.company.id].licenses.push({
            value: l.id.toString(),
            text: `${l.number} - ${l.facility.name}`,
          });
        });
        for (let k in companies) {
          let company = companies[k];
          company.licenses = company.licenses.sort((first, second) => {
            let name1 = first.text.toLowerCase(),
                name2 = second.text.toLowerCase();
            if (name1 < name2) {
              return -1;
            }
            if (name1 > name2) {
              return 1;
            }
            return 0;
          });
          licensesByCompanies.push(company);
        }
      }

      return licensesByCompanies;
    }
  },
  methods: {
    loadOrders() {
      this.loading = true;
      let params = {
        ...this.filters,
        per_page: this.per_page,
        page: this.page,
      }
      this.$api.get(API_ROUTES.ADMIN_ORDERS, {
        params: params
      }).then(({data}) => {
        this.orders = data.orders;
        this.total_in_pages = data.meta.total;
      }).catch(({response}) => {

      }).finally(() => {
        this.loading = false;
      });
    },
    loadLicenses() {
      this.$api.get(API_ROUTES.ADMIN_LICENSES, {
        params: {
          hide_inactive_companies: 1
        }
      }).then(({data}) => {
        this.licenses = data.licenses;
      });
    },
    toggleFilterValue(filter, value) {
      if (value === '') {
        if (this.filters[filter].indexOf('') < 0) {
          this.filters[filter] = [''];
          this.page = 1;
          this.loadOrders();
        }
      } else {
        let index = this.filters[filter].indexOf(value);
        if (index >= 0) {
          this.filters[filter].splice(index, 1);
          if (this.filters[filter].length === 0) {
            this.filters[filter] = [''];
          }
        } else {
          this.filters[filter].push(value);
          let index = this.filters[filter].indexOf('');
          if (index >= 0) {
            this.filters[filter].splice(index, 1);
          }
        }
        this.page = 1;
        this.loadOrders();
      }
    },
    dateFilterChanged(type, value) {
      if (type === 'start' && value && this.filters.date_end) {
        let startDate = moment(value),
            endDate = moment(this.filters.date_end);
        if (startDate.isAfter(endDate)) {
          this.filters.date_end = value;
        }
      } else if (type === 'end' && value && this.filters.date_start) {
        let startDate = moment(this.filters.date_start),
            endDate = moment(value);
        if (startDate.isAfter(endDate)) {
          this.filters.date_start = value;
        }
      }
      this.page = 1;
      this.loadOrders();
    },
    inputFilterChanged(filter, value) {
      if (this.timeouts[filter]) {
        clearTimeout(this.timeouts[filter]);
      }
      if (value !== this.prev_filters[filter]) {
        this.timeouts[filter] = setTimeout(() => {
          this.prev_filters[filter] = value;
          this.page = 1;
          this.loadOrders();
        }, 1500);
      }
    },
    perPageFilterChanged() {
      this.page = 1;
      this.loadOrders();
    },
    updateQueryParams() {
      let queryParams = {...this.$route.query};

      if (this.page > 1) {
        queryParams['page'] = this.page;
      } else {
        delete queryParams['page'];
      }
      if (this.per_page !== this.default_per_page) {
        queryParams['per_page'] = this.per_page;
      } else {
        delete queryParams['per_page'];
      }
      ['statuses', 'types', 'sources', 'sellers', 'buyers'].forEach(filter => {
        let values = this.filters[filter],
            queryArg = `${filter}[]`;
        if (values.length === 0 || values[0] === '') {
          delete queryParams[queryArg];
        } else {
          queryParams[queryArg] = values;
        }
      });
      ['number', 'date_start', 'date_end'].forEach(filter => {
        let value = this.filters[filter];
        if (value && value.length > 0) {
          queryParams[filter] = value;
        } else {
          delete queryParams[filter];
        }
      });

      if (JSON.stringify(queryParams) !== JSON.stringify(this.$route.query)) {
        this.$router.push({query: queryParams});
      }
    }
  },
  created() {
    this.loadOrders();
    this.loadLicenses();
  },
  watch: {
    page() {
      this.updateQueryParams();
    },
    per_page() {
      this.updateQueryParams();
    },
    'filters.number': function () {
      this.updateQueryParams();
    },
    'filters.statuses': function () {
      this.updateQueryParams();
    },
    'filters.types': function () {
      this.updateQueryParams();
    },
    'filters.sources': function () {
      this.updateQueryParams();
    },
    'filters.sellers': function () {
      this.updateQueryParams();
    },
    'filters.buyers': function () {
      this.updateQueryParams();
    },
    'filters.date_start': function () {
      this.updateQueryParams();
    },
    'filters.date_end': function () {
      this.updateQueryParams();
    }
  }
}
</script>
